import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type ApiError } from '@/lib/api';

export interface Skill {
  id: string;
  name: string;
  category: string | null;
}

export interface SkillGap {
  skillId: string;
  name: string;
  category: string | null;
  peopleShort: number;
  averageGap: number;
}

export interface EmployeeSkill {
  skillId: string;
  name: string;
  category: string | null;
  currentLevel: number;
  requiredLevel: number;
  gap: number;
}

export type PipStatus = 'ACTIVE' | 'COMPLETED_SUCCESS' | 'COMPLETED_FAILED' | 'CANCELLED';

export interface Pip {
  id: string;
  employeeId: string;
  employeeName: string;
  departmentName: string | null;
  createdByName: string | null;
  reason: string;
  objectives: string | null;
  startsOn: string;
  endsOn: string;
  status: PipStatus;
  outcome: string | null;
}

export interface SuccessionCandidate {
  employeeId: string;
  name: string;
  departmentName: string | null;
  designationTitle: string | null;
  rating: number;
  promotionRecommended: boolean;
  leadsTeam: boolean;
}

export const useSkills = () =>
  useQuery<Skill[], ApiError>({
    queryKey: ['skills'],
    queryFn: () => api.get<Skill[]>('/skills'),
    staleTime: 5 * 60_000,
  });

export const useSkillGaps = (enabled = true) =>
  useQuery<SkillGap[], ApiError>({
    queryKey: ['skills', 'gaps'],
    queryFn: () => api.get<SkillGap[]>('/skills/gaps'),
    enabled,
  });

export const useEmployeeSkills = (employeeId: string | undefined) =>
  useQuery<EmployeeSkill[], ApiError>({
    queryKey: ['skills', 'employee', employeeId],
    queryFn: () => api.get<EmployeeSkill[]>(`/employees/${employeeId}/skills`),
    enabled: Boolean(employeeId),
  });

export function useCreateSkill() {
  const qc = useQueryClient();
  return useMutation<Skill, ApiError, { name: string; category?: string | null }>({
    mutationFn: (b) => api.post<Skill>('/skills', b),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['skills'] }),
  });
}

export function useSetEmployeeSkill() {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    ApiError,
    { employeeId: string; skillId: string; currentLevel: number; requiredLevel: number }
  >({
    mutationFn: (b) => api.post('/employees/skills', b),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['skills'] }),
  });
}

export const usePips = (status?: PipStatus) =>
  useQuery<Pip[], ApiError>({
    queryKey: ['pips', status ?? 'all'],
    queryFn: () => api.get<Pip[]>(`/pips${status ? `?status=${status}` : ''}`),
  });

export function useCreatePip() {
  const qc = useQueryClient();
  return useMutation<
    Pip,
    ApiError,
    {
      employeeId: string;
      reason: string;
      objectives?: string | null;
      startsOn: string;
      endsOn: string;
    }
  >({
    mutationFn: (b) => api.post<Pip>('/pips', b),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pips'] });
      // Starting a plan flips the employee to ON_PIP, so the directory is stale.
      void qc.invalidateQueries({ queryKey: ['people'] });
    },
  });
}

export function useClosePip() {
  const qc = useQueryClient();
  return useMutation<Pip, ApiError, { id: string; status: PipStatus; outcome?: string | null }>({
    mutationFn: ({ id, ...b }) => api.patch<Pip>(`/pips/${id}/close`, b),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pips'] });
      void qc.invalidateQueries({ queryKey: ['people'] });
    },
  });
}

export const useSuccession = (enabled = true) =>
  useQuery<SuccessionCandidate[], ApiError>({
    queryKey: ['succession'],
    queryFn: () => api.get<SuccessionCandidate[]>('/succession'),
    enabled,
  });
